import { apiClient, logger } from '@/lib/local-platform';
import type {
  SpeechTopicListResp,
  SpeechTopicDetail,
  CreateSpeechTopicReq,
  CreateSpeechTopicResp,
  UpdateSpeechTopicReq,
  UpdateSpeechTopicResp,
  DeleteSpeechTopicResp,
  ReorderSpeechTopicsResp,
  AdminSpeechTopicListResp,
  AdminSpeechTopicImportPreviewResp,
  AdminSpeechTopicImportCommitReq,
  AdminSpeechTopicImportCommitResp,
  ContentCategory,
  SuitableGrade,
} from '@shared/api.interface';

function getAdminHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { 'x-admin-token': token } : {};
}

// 获取演讲主题列表
export async function getSpeechTopics(params: {
  search?: string;
  category?: ContentCategory;
  grade?: SuitableGrade;
  page?: number;
  pageSize?: number;
}): Promise<SpeechTopicListResp> {
  try {
    const response = await apiClient({
      url: '/api/speech-topics',
      method: 'GET',
      params: {
        search: params.search,
        category: params.category,
        grade: params.grade,
        page: params.page || 1,
        pageSize: params.pageSize || 20,
      },
    });
    return response.data;
  } catch (error) {
    logger.error('获取演讲主题列表失败', error);
    throw error;
  }
}

// 获取演讲主题详情
export async function getSpeechTopicById(id: string): Promise<SpeechTopicDetail> {
  try {
    const response = await apiClient({
      url: `/api/speech-topics/${id}`,
      method: 'GET',
    });
    return response.data;
  } catch (error) {
    logger.error('获取演讲主题详情失败', error);
    throw error;
  }
}

// 创建演讲主题
export async function createSpeechTopic(data: CreateSpeechTopicReq): Promise<CreateSpeechTopicResp> {
  try {
    const response = await apiClient({
      url: '/api/speech-topics',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('创建演讲主题失败', error);
    throw error;
  }
}

// 更新演讲主题
export async function updateSpeechTopic(
  id: string,
  data: UpdateSpeechTopicReq
): Promise<UpdateSpeechTopicResp> {
  try {
    const response = await apiClient({
      url: `/api/speech-topics/${id}`,
      method: 'PATCH',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('更新演讲主题失败', error);
    throw error;
  }
}

// 删除演讲主题
export async function deleteSpeechTopic(id: string): Promise<DeleteSpeechTopicResp> {
  try {
    const response = await apiClient({
      url: `/api/speech-topics/${id}`,
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('删除演讲主题失败', error);
    throw error;
  }
}

export async function reorderSpeechTopics(topicIds: string[]): Promise<ReorderSpeechTopicsResp> {
  const response = await apiClient({
    url: '/api/speech-topics/admin/reorder',
    method: 'PATCH',
    data: { topic_ids: topicIds },
    headers: getAdminHeaders(),
  });
  return response.data;
}

export async function getAdminSpeechTopics(params: {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
} = {}): Promise<AdminSpeechTopicListResp> {
  const response = await apiClient({
    url: '/api/speech-topics/admin/list',
    method: 'GET',
    params,
    headers: getAdminHeaders(),
  });
  return response.data;
}

export async function downloadSpeechTopicImportTemplate(): Promise<void> {
  const response = await apiClient({
    url: '/api/speech-topics/admin/import-template',
    method: 'GET',
    responseType: 'blob',
    headers: getAdminHeaders(),
  });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = '主题批量导入模板.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function previewSpeechTopicImport(file: File): Promise<AdminSpeechTopicImportPreviewResp> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient({
    url: '/api/speech-topics/admin/import/preview',
    method: 'POST',
    data: formData,
    headers: getAdminHeaders(),
  });
  return response.data;
}

export async function commitSpeechTopicImport(
  data: AdminSpeechTopicImportCommitReq
): Promise<AdminSpeechTopicImportCommitResp> {
  const response = await apiClient({
    url: '/api/speech-topics/admin/import/commit',
    method: 'POST',
    data,
    headers: getAdminHeaders(),
  });
  return response.data;
}
